import { formatShortDate, isResponseError } from "@shared";

import { SaveSrvProceedingRequest, SrvProceedingListItemResponse, SrvProceedingResponse } from "@/api";
import { ITranslation } from "@/translations";

import { SrvCourseEntryFormValues, SrvProceedingFormValues } from "./SrvProceedingTab.types";

export const SRV_QUOTA_MIN = 0;
export const SRV_QUOTA_MAX = 100;

export const EMPTY_SRV_PROCEEDING: SrvProceedingFormValues = {
  applicationDate: null,
  counselingType: null,
  openingDate: null,
  annulmentDate: null,
  courtId: null,
  fileNumber: "",
  trusteeId: "",
  insolvencyAdministratorId: "",
  offeredQuota: null,
  acceptedQuota: null,
  proceedingCosts: null,
  remark: "",
  courseEntries: [],
};

export function mapResponseToFormValues(response: SrvProceedingResponse): SrvProceedingFormValues {
  return {
    applicationDate: response.applicationDate ?? null,
    counselingType: response.counselingType ?? null,
    openingDate: response.openingDate ?? null,
    annulmentDate: response.annulmentDate ?? null,
    courtId: response.courtId ?? null,
    fileNumber: response.fileNumber ?? "",
    trusteeId: response.trusteeId ?? "",
    insolvencyAdministratorId: response.insolvencyAdministratorId ?? "",
    offeredQuota: response.offeredQuota ?? null,
    acceptedQuota: response.acceptedQuota ?? null,
    proceedingCosts: response.proceedingCosts ?? null,
    remark: response.remark ?? "",
    courseEntries: response.courseEntries.map((entry): SrvCourseEntryFormValues => ({
      id: entry.id,
      date: entry.dateUtc,
      entryType: entry.entryType ?? null,
      result: entry.result ?? null,
      remark: entry.remark ?? "",
    })),
  };
}

export function mapFormValuesToRequest(values: SrvProceedingFormValues): SaveSrvProceedingRequest {
  const validEntries = values.courseEntries.filter(
    (entry): entry is SrvCourseEntryFormValues & { date: Date } => entry.date !== null
  );

  return {
    applicationDate: values.applicationDate,
    counselingType: values.counselingType,
    openingDate: values.openingDate,
    annulmentDate: values.annulmentDate,
    courtId: values.courtId,
    fileNumber: values.fileNumber || null,
    trusteeId: values.trusteeId || null,
    insolvencyAdministratorId: values.insolvencyAdministratorId || null,
    offeredQuota: values.offeredQuota,
    acceptedQuota: values.acceptedQuota,
    proceedingCosts: values.proceedingCosts,
    remark: values.remark || null,
    courseEntries: validEntries.map((entry) => ({
      id: entry.id ?? null,
      dateUtc: entry.date,
      entryType: entry.entryType,
      result: entry.result,
      remark: entry.remark || null,
    })),
  };
}

const TRANSLATION_BASE = "clients.client.debtSettlement.srvProceeding.list";

/** The navigation title: the Aktenzahl the business side knows a Verfahren by, else its opening date. */
export function getSrvProceedingTitle(
  proceeding: Pick<SrvProceedingListItemResponse, "fileNumber" | "openingDate">,
  t: ITranslation
): string {
  if (proceeding.fileNumber) {
    return t(`${TRANSLATION_BASE}.titleWithFileNumber`, { fileNumber: proceeding.fileNumber });
  }

  if (proceeding.openingDate) {
    return t(`${TRANSLATION_BASE}.titleWithOpeningDate`, { date: formatShortDate(proceeding.openingDate) });
  }

  return t(`${TRANSLATION_BASE}.title`);
}

function normalizeFileNumber(value: string): string {
  return value.replace(/\s+/g, "").toLowerCase();
}

// The stored form of an Aktenzahl, as edicts and court letters print it ("025 012 S 00345/25p"): court prefix,
// zero-padded division, register, zero-padded sequence, year and an optional check letter.
const FULL_FILE_NUMBER_PATTERN = /^[0-9]{3} ([0-9]{3}) ([A-Z][a-zA-Z]{0,2}) ([0-9]{5})\/([0-9]{2}[a-zA-Z]?)$/;

/** A term in the full form turned into the short form the list shows ("12 S 345/25p"), like SrvFileNumber.ToShort. */
function toShortFileNumber(term: string): string {
  const match = FULL_FILE_NUMBER_PATTERN.exec(term.trim());
  return match ? `${Number(match[1])} ${match[2]} ${Number(match[3])}/${match[4]}` : term;
}

/**
 * The Verfahren whose Aktenzahl contains the search term; spacing and case do not matter, and a term pasted
 * in the full form matches the short form the list shows.
 */
export function filterByFileNumber(
  proceedings: SrvProceedingListItemResponse[] | undefined,
  term: string | null
): SrvProceedingListItemResponse[] | undefined {
  if (!proceedings || !term) {
    return proceedings;
  }

  const needle = normalizeFileNumber(toShortFileNumber(term));
  return proceedings.filter((p) => p.fileNumber && normalizeFileNumber(p.fileNumber).includes(needle));
}

const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;

/**
 * The message for a failed write of a Verfahren: a concurrent change and a deleted Verfahren say that
 * reloading helps, anything else falls back to <paramref name="fallback"/>.
 */
export function getSrvProceedingErrorMessage(error: unknown, t: ITranslation, fallback: string): string {
  if (isResponseError(error)) {
    if (error.response.status === HTTP_CONFLICT) {
      return t("clients.client.debtSettlement.srvProceeding.conflict");
    }

    if (error.response.status === HTTP_NOT_FOUND) {
      return t("clients.client.debtSettlement.srvProceeding.notFound");
    }
  }

  return fallback;
}
